import { useMenuLinks } from '../providers/MenuLinksProvider';
import { useYear } from '../providers/YearProvider';
import { getChapters, BASE_URL } from '../lib/const';
import { LinkWChart, LinkWChartTopic } from '../types/link';

export type ChapterType = 'state_view' | 'national_view' | 'donor_gaps' | 'final_analysis';
export type InternalChapter = 'candidates' | 'opportunities' | 'donors' | 'analysis';

interface ChapterConfig {
	type: ChapterType;
	chapter: InternalChapter;
	header: string;
	subheader: string;
	url: string;
	disabled: boolean;
	disabledText: string | undefined;
	links: Array<{ href: string; label: string }>;
}

interface UseChapterConfigReturn {
	getChapterData: (type: ChapterType) => ChapterConfig | undefined;
	getEnabledChapters: (excludeCurrent?: InternalChapter) => ChapterConfig[];
	getLandingPageUrl: () => string | null;
	hasLandingPage: boolean;
}

// Mapping from MenuLink types to internal chapter identifiers
const CHAPTER_TYPE_MAP: Record<ChapterType, {
	chapter: InternalChapter;
	header: string;
	subheader: string;
	fallbackUrlKey: 'state' | 'national' | 'donors' | 'analysis';
}> = {
	'state_view': {
		chapter: 'candidates',
		header: 'The State View',
		subheader: 'A Deep Dive into 10 States',
		fallbackUrlKey: 'state'
	},
	'national_view': {
		chapter: 'opportunities',
		header: 'The National View',
		subheader: 'Congressional Elections',
		fallbackUrlKey: 'national'
	},
	'donor_gaps': {
		chapter: 'donors',
		header: 'Donor Gaps',
		subheader: 'Demographic Analysis',
		fallbackUrlKey: 'donors'
	},
	'final_analysis': {
		chapter: 'analysis',
		header: 'Final Analysis',
		subheader: 'Congressional & State Elections',
		fallbackUrlKey: 'analysis'
	}
};

export const useChapterConfig = (): UseChapterConfigReturn => {
	const { landingPageLinks, getChapterLink } = useMenuLinks();
	const { year } = useYear();
	const CHAPTERS = getChapters(year);

	/**
	 * Get chapter configuration data for a specific chapter type
	 */
	const getChapterData = (type: ChapterType): ChapterConfig | undefined => {
		const menuLink = getChapterLink(type);

		if (!menuLink) {
			return undefined;
		}

		const mapping = CHAPTER_TYPE_MAP[type];
		const chapterConst = CHAPTERS[mapping.fallbackUrlKey];

		// Construct URL from MenuLink or fall back to CHAPTERS constant
		const url = menuLink.link_path
			? menuLink.link_path.replace(`${BASE_URL}`, '') // Remove BASE_URL if present
			: chapterConst.url;

		// Map section links from CHAPTERS constant and add BASE_URL
		const links = (chapterConst.links as Array<LinkWChart | LinkWChartTopic>).map((link) => ({
			href: `${BASE_URL}${url}/?sectionAnchor=${link.href}`,
			label: link.label
		}));

		return {
			type,
			chapter: mapping.chapter,
			header: mapping.header,
			subheader: mapping.subheader,
			url,
			disabled: menuLink.show_coming_soon_text,
			disabledText: menuLink.show_coming_soon_text ? menuLink.coming_soon_text : undefined,
			links
		};
	};

	/**
	 * Get all enabled chapters, optionally excluding the current chapter
	 */
	const getEnabledChapters = (excludeCurrent?: InternalChapter): ChapterConfig[] => {
		const allChapterTypes: ChapterType[] = ['state_view', 'national_view', 'donor_gaps', 'final_analysis'];

		const enabledChapters = allChapterTypes
			.map(type => getChapterData(type))
			.filter((config): config is ChapterConfig => config !== undefined);

		if (excludeCurrent) {
			return enabledChapters.filter(config => config.chapter !== excludeCurrent);
		}

		return enabledChapters;
	};

	/**
	 * Get the landing page URL if it exists
	 */
	const getLandingPageUrl = (): string | null => {
		// First check landingPageLinks
		if (landingPageLinks.length > 0 && landingPageLinks[0].link_path) {
			return landingPageLinks[0].link_path;
		}

		return null;
	};

	/**
	 * Check if a landing page exists
	 */
	const hasLandingPage = landingPageLinks.length > 0 && !!landingPageLinks[0].link_path;

	return {
		getChapterData,
		getEnabledChapters,
		getLandingPageUrl,
		hasLandingPage
	};
};